import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { SupportNavigationProvider } from "../../../features/admin/support-navigation/SupportNavigation";
import { useAuth } from "../../../features/auth/context/AuthContext";
import { useTranslation } from "../../../i18n";
import { lockBodyScroll, unlockBodyScroll } from "../../../lib/bodyScrollLock";
import { MerchantAccountControls } from "./MerchantAccountControls";
import { MerchantNavKey } from "./MerchantNavKey";
import { prefetchMerchantRoute } from "./merchantRoutePrefetch";
import { useMerchantShell } from "./MerchantShellContext";
import {
  appItems,
  bottomItems,
  businessItems,
  dashboardItem,
  desktopUtilityItems,
  isDesktopOnlyPath,
  isNavigationItemActive,
  mobileNavigationItems,
  mobileQuickActions,
  productionItems,
  visibleNavigationItems,
  wawiGroups,
  type MerchantNavigationItem,
} from "./merchantNavigation";
import { calculateVisibleBusinessKeyCount } from "./responsiveBusinessNavigation";
import { useMerchantNavigationBadges } from "./useMerchantNavigationBadges";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { AppEmptyState } from "../../ui/AppPage";
import "./merchant-shell.css";

const SortableBusinessNavigation = lazy(() => import("./SortableBusinessNavigation"));
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function MerchantBrandMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.25" />
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>
  );
}

function useResponsiveBusinessNavigation(itemCount: number) {
  const navigationRef = useRef<HTMLElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const navigation = navigationRef.current;
    if (!navigation) return undefined;

    let animationFrame = 0;
    const measure = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const shell = navigation.closest<HTMLElement>(".merchant-shell");
        const scroller = navigation.querySelector<HTMLElement>(".merchant-business-nav__scroller");
        const shellStyles = shell ? getComputedStyle(shell) : null;
        const navigationStyles = getComputedStyle(navigation);
        const scrollerStyles = scroller ? getComputedStyle(scroller) : null;
        const readPixels = (value: string | undefined, fallback: number) => {
          const parsed = Number.parseFloat(value || "");
          return Number.isFinite(parsed) ? parsed : fallback;
        };
        const nextCount = calculateVisibleBusinessKeyCount(
          navigation.getBoundingClientRect().width,
          itemCount,
          {
            keyWidth: readPixels(shellStyles?.getPropertyValue("--merchant-nav-key-width"), 144),
            keyGap: readPixels(scrollerStyles?.columnGap, 7),
            moreWidth: readPixels(shellStyles?.getPropertyValue("--merchant-nav-key-width"), 144),
            moreGap: readPixels(navigationStyles.columnGap, 5),
          },
        );
        setVisibleCount((current) => (current === nextCount ? current : nextCount));
      });
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => {
        cancelAnimationFrame(animationFrame);
        window.removeEventListener("resize", measure);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(navigation);
    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [itemCount]);

  return { navigationRef, visibleCount };
}

function MerchantAxisGlyph({ axis }: { axis: "focus" | "top" | "tools" }) {
  if (axis === "focus") {
    return (
      <svg className="merchant-axis-key__glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <g className="merchant-axis-key__glyph-motion">
          <path d="M4 10V4h6M16 16 7 7M7 12V7h5" />
        </g>
      </svg>
    );
  }

  if (axis === "top") {
    return (
      <svg className="merchant-axis-key__glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <g className="merchant-axis-key__glyph-motion">
          <path d="M6 4.5h12M12 17V8.5M8.5 12 12 8.5l3.5 3.5" />
        </g>
      </svg>
    );
  }

  return (
    <svg className="merchant-axis-key__glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <g className="merchant-axis-key__glyph-motion">
        <path d="M4.5 6v12M17 12H8.5M12 8.5 8.5 12l3.5 3.5" />
      </g>
    </svg>
  );
}

function MerchantRouteLoading() {
  const { t } = useTranslation();
  return (
    <div className="merchant-route-loading" role="status" aria-live="polite">
      <span className="merchant-route-loading__label">
        {t("layout.merchantShell.routeLoading")}
      </span>
      <div className="merchant-route-loading__progress" aria-hidden="true" />
      <div className="merchant-route-loading__skeleton" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function useDismissableMenu(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);
  return ref;
}

function DesktopDock() {
  const { t } = useTranslation();
  const location = useLocation();
  const { focusMode } = useMerchantShell();
  const dashboardActive = isNavigationItemActive(dashboardItem, location);

  return (
    <div
      className="merchant-shell__dock"
      aria-hidden={focusMode || undefined}
      inert={focusMode || undefined}
    >
      <Link
        to={dashboardItem.to}
        className="merchant-dock-home"
        aria-current={dashboardActive ? "page" : undefined}
        aria-label={t("layout.sidebar.logoAria")}
        onPointerEnter={() => prefetchMerchantRoute(dashboardItem.id)}
        onPointerDown={() => prefetchMerchantRoute(dashboardItem.id)}
        onFocus={() => prefetchMerchantRoute(dashboardItem.id)}
      >
        <span className="merchant-mark">
          <MerchantBrandMark />
        </span>
        <span className="merchant-dock-home__copy">
          <strong translate="no">Customy</strong>
          <span>{t("sidebar.dashboard")}</span>
        </span>
      </Link>
    </div>
  );
}

function DesktopAxisControls() {
  const { t } = useTranslation();
  const { topVisible, toolsVisible, focusMode, toggleTop, toggleTools, toggleFocus } =
    useMerchantShell();

  return (
    <div
      className="merchant-axis-controls"
      role="group"
      aria-label={t("layout.merchantShell.viewControls")}
      aria-hidden={focusMode || undefined}
      inert={focusMode || undefined}
    >
      <button
        type="button"
        className="merchant-axis-key merchant-axis-key--top"
        onClick={toggleTop}
        aria-pressed={!topVisible}
        aria-label={
          topVisible
            ? t("layout.merchantShell.hideBusinessNav")
            : t("layout.merchantShell.showBusinessNav")
        }
        title={
          topVisible
            ? t("layout.merchantShell.hideBusinessNav")
            : t("layout.merchantShell.showBusinessNav")
        }
      >
        <MerchantAxisGlyph axis="top" />
      </button>
      <button
        type="button"
        className="merchant-axis-key merchant-axis-key--tools"
        onClick={toggleTools}
        aria-pressed={!toolsVisible}
        aria-label={
          toolsVisible
            ? t("layout.merchantShell.hideToolsNav")
            : t("layout.merchantShell.showToolsNav")
        }
        title={
          toolsVisible
            ? t("layout.merchantShell.hideToolsNav")
            : t("layout.merchantShell.showToolsNav")
        }
      >
        <MerchantAxisGlyph axis="tools" />
      </button>
      <button
        type="button"
        className="merchant-axis-key merchant-axis-key--focus"
        onClick={toggleFocus}
        aria-label={t("layout.merchantShell.enterFocus")}
        title={t("layout.merchantShell.enterFocus")}
      >
        <MerchantAxisGlyph axis="focus" />
      </button>
    </div>
  );
}

function DesktopBusinessNavigation({
  orderedItems,
  badges,
}: {
  orderedItems: MerchantNavigationItem[];
  badges: ReturnType<typeof useMerchantNavigationBadges>;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { arranging, topVisible, focusMode, setArranging, setBusinessOrder, resetBusinessOrder } =
    useMerchantShell();
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const moreRef = useDismissableMenu(moreOpen, closeMore);
  const { navigationRef, visibleCount } = useResponsiveBusinessNavigation(orderedItems.length);
  const labels = useMemo(
    () => Object.fromEntries(orderedItems.map((item) => [item.id, t(item.labelKey)])),
    [orderedItems, t],
  );
  const visibleItems = arranging ? orderedItems : orderedItems.slice(0, visibleCount);
  const overflowItems = arranging ? [] : orderedItems.slice(visibleCount);

  return (
    <header
      className="merchant-shell__business-axis"
      aria-hidden={!topVisible || focusMode || undefined}
      inert={!topVisible || focusMode || undefined}
    >
      <nav
        ref={navigationRef}
        className="merchant-business-nav"
        aria-label={t("layout.merchantShell.businessNavAria")}
      >
        <div className="merchant-business-nav__scroller">
          {arranging ? (
            <Suspense
              fallback={
                <div className="merchant-arrange-loading">
                  {t("layout.merchantShell.arrangeLoading")}
                </div>
              }
            >
              <SortableBusinessNavigation
                items={orderedItems}
                labels={labels}
                badges={badges}
                onOrderChange={setBusinessOrder}
                instructions={t("layout.merchantShell.arrangeInstructions")}
              />
            </Suspense>
          ) : (
            visibleItems.map((item) => (
              <MerchantNavKey
                key={item.id}
                item={item}
                label={labels[item.id]}
                active={isNavigationItemActive(item, location)}
                badge={badges[item.id]}
                orientation="horizontal"
              />
            ))
          )}
        </div>
        {!arranging && orderedItems.length > 0 && (
          <div className="merchant-more" ref={moreRef}>
            <button
              type="button"
              className="merchant-nav-key merchant-nav-key--horizontal merchant-nav-key--more"
              onClick={() => setMoreOpen((value) => !value)}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
            >
              <span className="material-symbols-outlined merchant-nav-key__icon" aria-hidden="true">
                more_horiz
              </span>
              <span className="merchant-nav-key__label">{t("layout.merchantShell.more")}</span>
            </button>
            {moreOpen && (
              <div className="merchant-menu merchant-more__menu" role="menu">
                {/* Ausgelagerte Bereiche behalten ihre Gruppen (Verkauf, Buchhaltung, …),
                    damit die Hierarchie im Menü nicht verloren geht. */}
                {wawiGroups.map((group) => {
                  const groupItems = overflowItems.filter((item) =>
                    group.items.some((groupItem) => groupItem.id === item.id),
                  );
                  if (!groupItems.length) return null;
                  return (
                    <div key={group.key} className="merchant-more__group">
                      {group.items.length > 1 && (
                        <div className="merchant-menu__title">{t(group.labelKey)}</div>
                      )}
                      {groupItems.map((item) => (
                        <Link
                          key={item.id}
                          to={item.to}
                          role="menuitem"
                          aria-current={
                            isNavigationItemActive(item, location) ? "page" : undefined
                          }
                          onClick={closeMore}
                          onPointerEnter={() => prefetchMerchantRoute(item.id)}
                          onPointerDown={() => prefetchMerchantRoute(item.id)}
                          onFocus={() => prefetchMerchantRoute(item.id)}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            {item.icon}
                          </span>
                          {t(item.labelKey)}
                        </Link>
                      ))}
                    </div>
                  );
                })}
                {overflowItems.length > 0 && <div className="merchant-menu__separator" />}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMore();
                    setArranging(true);
                  }}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    drag_indicator
                  </span>
                  {t("layout.merchantShell.arrangeNavigation")}
                </button>
              </div>
            )}
          </div>
        )}
      </nav>

      {arranging ? (
        <div className="merchant-arrange-actions">
          <button type="button" className="merchant-text-key" onClick={resetBusinessOrder}>
            {t("layout.merchantShell.resetOrder")}
          </button>
          <button
            type="button"
            className="merchant-text-key merchant-text-key--primary"
            onClick={() => setArranging(false)}
          >
            {t("layout.merchantShell.done")}
          </button>
        </div>
      ) : (
        <MerchantAccountControls />
      )}
    </header>
  );
}

function DesktopToolNavigation({
  badges,
}: {
  badges: ReturnType<typeof useMerchantNavigationBadges>;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { toolsVisible, focusMode } = useMerchantShell();
  const tools = visibleNavigationItems(productionItems, isAdmin);
  const apps = visibleNavigationItems(appItems, isAdmin);
  const utilities = visibleNavigationItems(desktopUtilityItems, isAdmin);

  return (
    <aside
      className="merchant-shell__tool-axis"
      aria-label={t("layout.merchantShell.toolsNavAria")}
      aria-hidden={!toolsVisible || focusMode || undefined}
      inert={!toolsVisible || focusMode || undefined}
    >
      <div className="merchant-tool-axis__heading">
        <span>{t("layout.sidebar.sectionProduction")}</span>
        <span>{t("layout.merchantShell.toolsShort")}</span>
      </div>
      <nav className="merchant-tool-axis__nav" aria-label={t("layout.sidebar.sectionProduction")}>
        {tools.map((item) => (
          <MerchantNavKey
            key={item.id}
            item={item}
            label={t(item.labelKey)}
            active={isNavigationItemActive(item, location)}
            badge={badges[item.id]}
          />
        ))}
      </nav>
      <div className="merchant-tool-axis__apps">
        <span className="merchant-tool-axis__rail" aria-hidden="true" />
        <span>{t("layout.merchantShell.apps")}</span>
      </div>
      <nav
        className="merchant-tool-axis__app-nav"
        aria-label={t("layout.merchantShell.appsNavAria")}
      >
        {apps.map((item) => (
          <MerchantNavKey
            key={item.id}
            item={item}
            label={t(item.labelKey)}
            active={isNavigationItemActive(item, location)}
            badge={badges[item.id]}
          />
        ))}
      </nav>
      <nav
        className="merchant-tool-axis__utility"
        aria-label={t("layout.merchantShell.utilityNavAria")}
      >
        {utilities.map((item) => (
          <MerchantNavKey
            key={item.id}
            item={item}
            label={t(item.labelKey)}
            active={isNavigationItemActive(item, location)}
            badge={badges[item.id]}
          />
        ))}
      </nav>
    </aside>
  );
}

function DesktopChrome({
  orderedBusinessItems,
  badges,
}: {
  orderedBusinessItems: MerchantNavigationItem[];
  badges: ReturnType<typeof useMerchantNavigationBadges>;
}) {
  const { t } = useTranslation();
  const { focusMode, toggleFocus } = useMerchantShell();
  return (
    <>
      <DesktopDock />
      <DesktopAxisControls />
      <DesktopBusinessNavigation orderedItems={orderedBusinessItems} badges={badges} />
      <DesktopToolNavigation badges={badges} />
      {focusMode && (
        <button
          type="button"
          className="merchant-focus-restore"
          onClick={toggleFocus}
          aria-label={t("layout.merchantShell.exitFocus")}
          title={t("layout.merchantShell.exitFocus")}
        >
          <MerchantAxisGlyph axis="focus" />
        </button>
      )}
    </>
  );
}

function MobileDrawer({
  open,
  onClose,
  badges,
}: {
  open: boolean;
  onClose: () => void;
  badges: ReturnType<typeof useMerchantNavigationBadges>;
}) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const renderItem = (item: MerchantNavigationItem) => (
    <MerchantNavKey
      key={item.id}
      item={item}
      label={t(item.labelKey)}
      active={isNavigationItemActive(item, location)}
      badge={badges[item.id]}
      onNavigate={onClose}
    />
  );

  return (
    <div className="merchant-mobile-drawer" role="presentation">
      <button
        type="button"
        className="merchant-mobile-drawer__scrim"
        onClick={onClose}
        aria-label={t("layout.mobileNav.closeAria")}
      />
      <div
        ref={panelRef}
        className="merchant-mobile-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="merchant-mobile-menu-title"
      >
        <div className="merchant-mobile-drawer__handle" aria-hidden="true" />
        <div className="merchant-mobile-drawer__header">
          <div>
            <strong id="merchant-mobile-menu-title">{t("layout.mobileNav.menuTitle")}</strong>
            <span>{t("layout.merchantShell.mobileMenuHint")}</span>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="merchant-icon-key"
            onClick={onClose}
            aria-label={t("layout.mobileNav.closeAria")}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>
        <div className="merchant-mobile-drawer__content">
          {visibleNavigationItems(mobileQuickActions, isAdmin).length > 0 && (
            <section>
              <h2>{t("layout.merchantShell.quickActions")}</h2>
              {visibleNavigationItems(mobileQuickActions, isAdmin).map(renderItem)}
            </section>
          )}
          <section>
            <h2>{t("layout.sidebar.sectionProduction")}</h2>
            {visibleNavigationItems(productionItems, isAdmin).map(renderItem)}
          </section>
          {visibleNavigationItems(appItems, isAdmin).length > 0 && (
            <section>
              <h2>{t("layout.merchantShell.apps")}</h2>
              {visibleNavigationItems(appItems, isAdmin).map(renderItem)}
            </section>
          )}
          {wawiGroups.map((group) => {
            const visibleItems = visibleNavigationItems(group.items, isAdmin).filter(
              (item) => !isDesktopOnlyPath(item.to),
            );
            if (!visibleItems.length) return null;
            return (
              <section key={group.key}>
                <h2>{t(group.labelKey)}</h2>
                {visibleItems.map(renderItem)}
              </section>
            );
          })}
          <section>
            <h2>{t("layout.merchantShell.accountAndFiles")}</h2>
            {bottomItems.map(renderItem)}
          </section>
        </div>
      </div>
    </div>
  );
}

function MobileChrome({
  badges,
  currentItem,
}: {
  badges: ReturnType<typeof useMerchantNavigationBadges>;
  currentItem?: MerchantNavigationItem;
}) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const quickItems = mobileNavigationItems(isAdmin);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(closeMenu, [closeMenu, location.pathname]);

  return (
    <>
      <header className="merchant-mobile-header">
        <div className="merchant-mobile-header__leading">
          <Link
            to="/dashboard"
            className="merchant-mobile-brand"
            aria-label={t("layout.sidebar.logoAria")}
            onPointerEnter={() => prefetchMerchantRoute(dashboardItem.id)}
            onPointerDown={() => prefetchMerchantRoute(dashboardItem.id)}
            onFocus={() => prefetchMerchantRoute(dashboardItem.id)}
          >
            <MerchantBrandMark size={26} />
          </Link>
          <span className="merchant-mobile-header__context">
            {currentItem ? t(currentItem.labelKey) : "Customy"}
          </span>
        </div>
        <MerchantAccountControls compact />
      </header>

      <nav className="merchant-mobile-bottom" aria-label={t("layout.mobileNav.navAria")}>
        {quickItems.map((item) => {
          const label = t(item.mobileLabelKey || item.labelKey);
          return (
            <MerchantNavKey
              key={item.id}
              item={item}
              label={label}
              active={isNavigationItemActive(item, location)}
              badge={badges[item.id]}
              orientation="mobile"
            />
          );
        })}
        {/* Rechtshänder: Menü und Schnellaktionen sitzen unten rechts im Daumenbereich. */}
        <button
          type="button"
          className="merchant-mobile-menu-key"
          onClick={() => setMenuOpen(true)}
          aria-label={t("layout.mobileNav.openMenuAria")}
          aria-expanded={menuOpen}
          aria-haspopup="dialog"
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            apps
          </span>
          <span>{t("layout.mobileNav.more")}</span>
        </button>
      </nav>
      <MobileDrawer open={menuOpen} onClose={closeMenu} badges={badges} />
    </>
  );
}

export default function MerchantAppShell() {
  const { t } = useTranslation();
  const { isAdmin, userId } = useAuth();
  const location = useLocation();
  const { topVisible, toolsVisible, focusMode, businessOrder } = useMerchantShell();
  const badges = useMerchantNavigationBadges(isAdmin, t);
  const phone = useMediaQuery("(max-width: 767px)");
  const visibleBusiness = visibleNavigationItems(businessItems, isAdmin);
  const orderedBusinessItems = businessOrder
    .map((id) => visibleBusiness.find((item) => item.id === id))
    .filter((item): item is MerchantNavigationItem => Boolean(item));
  const allVisibleItems = [
    dashboardItem,
    ...visibleNavigationItems(productionItems, isAdmin),
    ...visibleNavigationItems(appItems, isAdmin),
    ...visibleBusiness,
    ...bottomItems,
  ];
  const currentItem = allVisibleItems.find((item) => isNavigationItemActive(item, location));
  const dataState = {
    "data-top-visible": String(topVisible && !focusMode),
    "data-tools-visible": String(toolsVisible && !focusMode),
    "data-focus": String(focusMode),
  };

  return (
    <SupportNavigationProvider key={userId || "anonymous"} userId={userId || "anonymous"}>
      <div className="merchant-shell" {...dataState}>
        <a href="#main-content" className="merchant-skip-link">
          {t("layout.skipToContent")}
        </a>
        <DesktopChrome orderedBusinessItems={orderedBusinessItems} badges={badges} />
        <MobileChrome badges={badges} currentItem={currentItem} />
        <main id="main-content" tabIndex={-1} className="merchant-shell__content">
          <Suspense fallback={<MerchantRouteLoading />}>
            {phone && isDesktopOnlyPath(location.pathname) ? (
              <AppEmptyState
                icon="desktop_windows"
                title={t("layout.merchantShell.desktopOnlyTitle")}
                description={t("layout.merchantShell.desktopOnlyBody")}
                primaryAction={
                  <Link to="/dashboard" className="text-sm font-semibold text-primary">
                    {t("layout.merchantShell.desktopOnlyBack")}
                  </Link>
                }
                className="mt-6"
              />
            ) : (
              <Outlet />
            )}
          </Suspense>
        </main>
      </div>
    </SupportNavigationProvider>
  );
}
